import { useEffect, useId, useRef, type ChangeEvent, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import type { OffertaMenu } from "@/api/preventivo";
import { cambiaQuota, quote, spreadPer, trova } from "@/lib/menuOfferte";
import { quotaMenu, spreadMenu } from "@/lib/preventivoFormato";
import { MENU_PCV, TORNA_CONDOMINIO } from "./scritte";
import { scadutaIl } from "./scritteLista";

interface Props {
  offerte: OffertaMenu[];
  /** L'offerta che vale adesso; `null` = nessuna ancora scelta. */
  valore: OffertaMenu | null;
  onScegli: (offertaId: string) => void;
  contesto: "condominio" | "contatore";
  /** Solo per un contatore con un'offerta propria: «Torna all'offerta del condominio». */
  onTorna?: () => void;
  /** Una scelta è in volo: menù spenti, nessuna seconda richiesta. */
  disabilitato?: boolean;
  /** Avviso di una scelta non salvata, sotto i menù. */
  avviso?: ReactNode;
  /** Nella riga sottile sotto la fornitura (tabella, 03/11): etichetta accanto al menù, alti 44 px, niente nome
   *  dell'offerta (solo «scaduta il …»); «Torna all'offerta del condominio» è un link. */
  inLinea?: boolean;
}

const SELECT =
  "h-12 w-full appearance-none rounded-lg border border-border bg-card pl-3 pr-9 text-base font-medium text-text cursor-pointer " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:cursor-not-allowed disabled:opacity-50";
const SELECT_IN_LINEA =
  "h-11 w-36 appearance-none rounded-lg border border-border bg-card pl-3 pr-8 text-sm font-medium text-text cursor-pointer " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:cursor-not-allowed disabled:opacity-50";
const FRECCIA = "pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground";

/** I due menù «PCV» e «Spread» (spec § 7.2 e § 8): lo spread offre solo i valori che esistono con la quota, così
 *  ogni scelta è un'offerta vera; sotto, nome, fornitore e «valida fino al …». */
export function SceltaOfferta({ offerte, valore, onScegli, contesto, onTorna, disabilitato = false, avviso, inLinea = false }: Props) {
  const id = useId();
  // un'offerta scaduta non è una voce dei menù: i menù restano senza scelta e sotto c'è il suo nome con «scaduta il …»
  const inMenu = valore?.scaduta_il ? null : valore;
  const quotaAttuale = inMenu?.quota == null ? null : Number(inMenu.quota);
  const spreadAttuale = inMenu?.spread == null ? null : Number(inMenu.spread);
  const spread = quotaAttuale === null ? [] : spreadPer(offerte, quotaAttuale);

  // Un menù spento perde il focus: alla risposta lo si rimette sul menù che ha fatto la scelta, e solo in questa
  // copia (scheda, griglia e pannello sono montati insieme e condividono lo stesso «in volo»).
  const scelto = useRef<HTMLSelectElement | null>(null);
  useEffect(() => {
    const el = scelto.current;
    if (disabilitato || el === null) return;
    scelto.current = null;
    if (!el.isConnected) return;
    if (document.activeElement === null || document.activeElement === document.body) el.focus();
  }, [disabilitato]);

  function scegli(el: HTMLSelectElement, id: string) {
    scelto.current = el;
    onScegli(id);
  }
  function suQuota(e: ChangeEvent<HTMLSelectElement>) {
    const o = cambiaQuota(offerte, inMenu ?? undefined, Number(e.target.value));
    if (o.id !== valore?.id) scegli(e.currentTarget, o.id);
  }
  function suSpread(e: ChangeEvent<HTMLSelectElement>) {
    if (quotaAttuale === null) return;
    const o = trova(offerte, quotaAttuale, Number(e.target.value));
    if (o && o.id !== valore?.id) scegli(e.currentTarget, o.id);
  }

  if (inLinea) {
    const menu = (etichetta: string, el: ReactNode, per: string) => (
      <span className="inline-flex items-center gap-2">
        <label htmlFor={per} className="text-xs font-medium text-muted-foreground">{etichetta}</label>
        <span className="relative block">
          {el}
          <ChevronDown size={14} aria-hidden="true" className={FRECCIA} />
        </span>
      </span>
    );
    return (
      <div data-contesto={contesto} className="flex flex-col items-end gap-1">
        <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-2">
          {menu(MENU_PCV, (
            <select id={`${id}-quota`} className={SELECT_IN_LINEA} value={quotaAttuale === null ? "" : String(quotaAttuale)}
              onChange={suQuota} disabled={disabilitato}>
              {quotaAttuale === null && <option value="" disabled>—</option>}
              {quote(offerte).map((q) => <option key={q} value={String(q)}>{quotaMenu(String(q))}</option>)}
            </select>
          ), `${id}-quota`)}
          {menu("Spread", (
            <select id={`${id}-spread`} className={SELECT_IN_LINEA} value={spreadAttuale === null ? "" : String(spreadAttuale)}
              onChange={suSpread} disabled={disabilitato || quotaAttuale === null}>
              {spreadAttuale === null && <option value="" disabled>—</option>}
              {spread.map((s) => <option key={s} value={String(s)}>{spreadMenu(String(s))}</option>)}
            </select>
          ), `${id}-spread`)}
        </div>
        {valore?.scaduta_il && (
          <p className="text-xs text-muted-foreground break-words">
            {valore.nome} · <span className="font-medium text-red-700">{scadutaIl(valore.scaduta_il)}</span>
          </p>
        )}
        {onTorna && (
          <button
            type="button"
            onClick={onTorna}
            disabled={disabilitato}
            className="relative rounded-sm text-xs font-medium text-brand-dark underline underline-offset-2 cursor-pointer hover:text-brand after:absolute after:-inset-x-1 after:-inset-y-3 after:content-[''] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:cursor-not-allowed disabled:opacity-50"
          >
            {TORNA_CONDOMINIO}
          </button>
        )}
        {avviso && <div className="mt-1">{avviso}</div>}
      </div>
    );
  }

  return (
    <div data-contesto={contesto}>
      <div className="grid grid-cols-2 gap-3">
        <div className="min-w-0">
          <label htmlFor={`${id}-quota`} className="mb-1 block text-xs font-medium text-muted-foreground">{MENU_PCV}</label>
          <span className="relative block">
            <select
              id={`${id}-quota`}
              className={SELECT}
              value={quotaAttuale === null ? "" : String(quotaAttuale)}
              onChange={suQuota}
              disabled={disabilitato}
            >
              {quotaAttuale === null && <option value="" disabled>—</option>}
              {quote(offerte).map((q) => <option key={q} value={String(q)}>{quotaMenu(String(q))}</option>)}
            </select>
            <ChevronDown size={16} aria-hidden="true" className={FRECCIA} />
          </span>
        </div>
        <div className="min-w-0">
          <label htmlFor={`${id}-spread`} className="mb-1 block text-xs font-medium text-muted-foreground">Spread</label>
          <span className="relative block">
            <select
              id={`${id}-spread`}
              className={SELECT}
              value={spreadAttuale === null ? "" : String(spreadAttuale)}
              onChange={suSpread}
              disabled={disabilitato || quotaAttuale === null}
            >
              {spreadAttuale === null && <option value="" disabled>—</option>}
              {spread.map((s) => <option key={s} value={String(s)}>{spreadMenu(String(s))}</option>)}
            </select>
            <ChevronDown size={16} aria-hidden="true" className={FRECCIA} />
          </span>
        </div>
      </div>
      {avviso && <div className="mt-2">{avviso}</div>}
      {/* sotto i menù solo il nome dell'offerta come lo scrive il CRM (spec § 8) */}
      {valore && (
        <p className="mt-2 text-sm text-muted-foreground break-words">
          {valore.nome}{valore.scaduta_il && <> · <span className="font-medium text-red-700">{scadutaIl(valore.scaduta_il)}</span></>}
        </p>
      )}
      {contesto === "contatore" && onTorna && (
        <button
          type="button"
          onClick={onTorna}
          disabled={disabilitato}
          className="mt-3 h-12 w-full rounded-lg border border-border bg-card px-3 text-sm font-medium text-text cursor-pointer hover:bg-surface transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:cursor-not-allowed disabled:opacity-50"
        >
          {TORNA_CONDOMINIO}
        </button>
      )}
    </div>
  );
}
